import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { AssetCheckForm } from '@/components/assetCheck/AssetCheckForm'
import { AssetCheckHistoryList } from '@/components/assetCheck/AssetCheckHistoryList'
import { AssetCheckResultCard } from '@/components/assetCheck/AssetCheckResultCard'
import { createAssetCheck, listAssetChecks } from '@/lib/assetCheckApi'
import { useToastStore } from '@/stores/toast'
import type { AssetCheck, CreateAssetCheckPayload } from '@/types/assetCheck'

interface AssetCheckPageProps {
  // Optional cluster list for override dropdown. For MVP, parent passes []
  // because we don't have a dedicated cluster endpoint yet.
  clusters?: Array<{ name: string; label: string }>
}

export function AssetCheckPage({ clusters = [] }: AssetCheckPageProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [latestResult, setLatestResult] = useState<AssetCheck | null>(null)

  const historyQuery = useQuery({
    queryKey: ['asset-check', 'list'],
    queryFn: () => listAssetChecks({ limit: 50 }),
  })

  const mutation = useMutation({
    mutationFn: (payload: CreateAssetCheckPayload) => createAssetCheck(payload),
    onSuccess: (data) => {
      setLatestResult(data)
      queryClient.invalidateQueries({ queryKey: ['asset-check', 'list'] })
      navigate(`/asset-check/${data.id}`, { replace: false })
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      const push = useToastStore.getState().pushToast
      if (status === 404) push({ kind: 'error', message: 'ISIN nicht gefunden bei yfinance' })
      else if (status === 503)
        push({ kind: 'error', message: 'Marktdaten temporär nicht verfügbar' })
      else if (status === 409)
        push({ kind: 'error', message: 'Keine aktive Strategie — erst /strategy aufrufen' })
      else if (status === 422) push({ kind: 'error', message: `Eingabe ungültig: ${detail ?? ''}` })
      else if (status === 429)
        push({ kind: 'error', message: 'Zu viele Checks. Bitte 1 Minute warten.' })
      else push({ kind: 'error', message: 'Asset-Check fehlgeschlagen' })
    },
  })

  return (
    <AppShell>
      <article className="fade-in max-w-4xl space-y-6" data-testid="asset-check-page">
        <h1 className="text-2xl font-bold text-slate-900">Asset-Check</h1>

        <AssetCheckForm
          clusters={clusters}
          onSubmit={(p) => mutation.mutate(p)}
          isSubmitting={mutation.isPending}
        />

        {mutation.isPending && (
          <div
            className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800"
            data-testid="loading-banner"
          >
            Lade Marktdaten für Portfolio + Kandidat … das kann 5–15 Sekunden dauern.
          </div>
        )}

        {latestResult && !mutation.isPending && <AssetCheckResultCard check={latestResult} />}

        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Historie</h2>
          {historyQuery.isLoading ? (
            <div className="text-sm text-slate-500">Lade …</div>
          ) : (
            <AssetCheckHistoryList items={historyQuery.data?.items ?? []} />
          )}
        </div>
      </article>
    </AppShell>
  )
}
